import { Field, ErrorMessage } from 'formik';
import { cn } from '@/utils/cn';

interface InputProps {
  name: string;
  type?:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'tel'
    | 'time'
    | 'date'
    | 'datetime-local'
    | 'hidden';
  placeholder?: string;
  label?: string;
  className?: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number | 'any';
  disabled?: boolean;
  value?: string | number;
  blockSpaces?: boolean;
  uppercase?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const Input = ({
  name,
  type = 'text',
  placeholder,
  label,
  className,
  required,
  min,
  max,
  step,
  disabled,
  value,
  blockSpaces,
  uppercase,
  onChange,
}: InputProps) => {
  return (
    <div className="w-full min-w-0">
      {label && (
        <label htmlFor={name} className="block text-sm font-bold text-gray-900 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <Field name={name}>
        {({ field, form }: any) => (
          <input
            id={name}
            {...field}
            type={type}
            placeholder={placeholder}
            min={type === 'number' && min !== undefined ? min : undefined}
            max={type === 'number' && max !== undefined ? max : undefined}
            step={type === 'number' && step !== undefined ? step : undefined}
            className={cn(
              'w-full min-w-0 border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm font-medium text-gray-900 outline-none focus:outline-none focus:border-[#FC480D] transition-colors duration-200',
              className,
              { 'bg-gray-100 cursor-not-allowed': disabled }
            )}
            disabled={disabled}
            onChange={(e) => {
              if (onChange) {
                onChange(e);
              }
              const raw = (e.target as HTMLInputElement).value;

              // Numeric handling: enforce min/max as before
              if (type === 'number' && raw !== '') {
                const num = Number(raw);
                if (!Number.isNaN(num)) {
                  if (min !== undefined && num < min) {
                    form.setFieldValue(name, String(min));
                    return;
                  }
                  if (max !== undefined && num > max) {
                    form.setFieldValue(name, String(max));
                    return;
                  }
                }
                // For numeric inputs, fall back to default change
                field.onChange(e);
                return;
              }

              // For non-number inputs, optionally transform the value
              let processed = raw;
              if (blockSpaces) processed = processed.replace(/ /g, '');
              if (uppercase) processed = processed.toUpperCase();

              // If processing changed the value, update Formik directly
              if (processed !== raw) {
                form.setFieldValue(name, processed);
                return;
              }

              // Otherwise, use Formik's default handler
              field.onChange(e);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              // Prevent typing space when blockSpaces is enabled
              if (blockSpaces) {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }
            }}
            onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
              const paste = e.clipboardData.getData('text') || '';
              let processed = paste;
              if (blockSpaces) processed = processed.replace(/ /g, '');
              if (uppercase) processed = processed.toUpperCase();
              if (processed !== paste) {
                e.preventDefault();
                // Insert cleaned paste at the end of current value
                form.setFieldValue(name, String(field.value || '') + processed);
              }
            }}
            {...(value !== undefined ? { value } : {})}
          />
        )}
      </Field>

      <ErrorMessage name={name} component="div" className="text-red-500 text-xs mt-1" />
    </div>
  );
};

export default Input;
